// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Operator settings, resolved (04 §15).
//
// > *"Real machines have an operator menu. So does this one, and it is where difficulty lives."*
//
// **It did not live here. It did not live anywhere.** `settings` has been in the format since M2,
// validated by both cores, and folded into the leaderboard key by `server/ts/settings.ts` since
// M4-19 — so two players on different settings have always ranked separately, on a difference that
// could not exist, because **nothing in either core ever read a settings block**. A grep for
// `ballSaveSeconds`, `extraBallEnabled`, `tiltWarnings` or `outlanePosts` across `core/`, `server/`
// and `src/` returned their own validation and their own tests and not one consumer.
//
// What the block was instead was a *second description* of what the rulesheet does. NEON NOVA
// declares `ballSaveSeconds: 12` beside a `ballStart` rule saying `enableBallSave: 12000`;
// `replayScore: 25000000` beside `rules.scoring.replayScore: 25000000`; `tiltWarnings: 2` beside a
// `CONTACTS_TO_TILT = 3` constant in `TiltBob.ts`. Three pairs of numbers that agree today because
// somebody typed them twice, with nothing anywhere holding them to each other — which is the shape
// this repository has recorded as *"a parameter name is a claim about its argument"*, one layer up:
// a whole block of claims, and no arguments.
//
// **The operator setting is the authority, and that is what an operator menu is.** A rule asking
// for ball save is the machine's *rules* saying "save the ball"; how long is the *machine's*
// business, set on the coin door, and a real rulesheet has no opinion about it. So where a machine
// declares a setting the engine reads, the declared value wins over the constant or the literal it
// used to be. Every one of NEON NOVA's declared values equals what the engine did before, which is
// why this changes no fixture — and `test/core/machine/settings.test.ts` asserts that equality
// rather than assuming it, because it is exactly the sort of coincidence that stops being true.
//
// `ballSaveSeconds` IS HERE NOW, AND THE ROUTE MATTERS MORE THAN THE ANSWER (M9-35)
// ---------------------------------------------------------------------------------
// It was *written, shipped into a test run, and taken back out* at M9-34. The reasoning above says
// the operator setting is the authority — a rulesheet asks for ball save and how long is set on the
// coin door — and applied to NEON NOVA that was wrong in a way reading could not have shown. The
// flagship's rules grant **five different ball saves**: 12 s at ball start, 20 s at each of two
// multiballs, 30 s at another, and 60 s in wizard mode. One operator number cannot mean all five,
// and forcing it flattened four of them; the C# replay parity family caught it immediately — ten of
// a hundred recorded games came out with a different score, two of them with **no score at all**.
//
// *The TypeScript suite passed. Every one of its 807 core tests passed, because nothing on this
// side replays a hundred recorded games — the 100-game family lives in `core/cs`. A change that
// "changes no fixture" is a claim about the fixtures that ran.*
//
// **So the author says which grant it governs**, with `{"enableBallSave": "operator"}` — 04 §11.4's
// one non-numeric action argument, and the *only* thing that reads this setting. The rest stay
// literal, because on a real machine they are the rulesheet's numbers and not the operator's. Both
// shipped machines now name it on their `ballStart` rule, where the literal they replaced was
// already equal to the declared default — 12 000 ms against 12 s on NEON NOVA, 20 000 against 20 on
// IRON FATHOM — so the change is behaviour-neutral at the defaults and honours the menu when moved.
// `settings.test.ts` asserts that equality rather than assuming it.
//
// It is resolved in `RuleEngine`, not here and not in `Game`: the `ballSave` effect's value has
// always meant *steps*, and a sentinel in a numeric field is a second meaning for one number.
//   * **`outlanePosts`** moves colliders. 05 §2 says the outlane width is operator-adjustable
//     through post position, and doing it needs the *format* to say which colliders a setting moves
//     and by how much — 04 §15 does not. It also moves the flagship's drain distribution, which is
//     a **published measurement** (05 §2: 1,428 left · 7,253 centre · 803 right over 10,000 balls),
//     so it is a physics change wearing a settings change's clothes and belongs in its own task
//     with its own soak.
//   * **`kickbackEnabled`**, which is NEON NOVA's own rather than one of §15's six. Reading it
//     means the rule language can ask about a setting, and the obvious cheap route — seeding a
//     `setting.*` variable into `VarStore` — changes `vars.snapshot()`, which is inside the
//     fingerprint every parity fixture is keyed on. A rule-language operand is the honest way and
//     it is a change to 04 §11.
//
// Pure. No clock, no RNG, no platform call: this reads a document and returns values.

/** What a setting may be. The three JSON scalars a `default` is allowed to hold. */
export type SettingValue = string | number | boolean

/** Every setting a machine declares, at a concrete value. */
export type ResolvedSettings = Readonly<Record<string, SettingValue>>

/**
 * A machine's settings at their declared defaults.
 *
 * **A machine with no settings block resolves to nothing at all**, and every reader below then
 * falls back to the constant or the literal it used before — so a definition written before this
 * existed behaves exactly as it did, which is the property that let this land without touching a
 * single fixture.
 *
 * Deliberately the same shape and the same reading as `server/ts/settings.ts`'s `defaultSettings`,
 * which computes the leaderboard key from the same block. Two readings of one block would be two
 * answers to *which machine is this*, and the board is keyed on the server's.
 */
export function resolveSettings(definition: unknown): ResolvedSettings {
  const block = (definition as { settings?: unknown } | null)?.settings
  if (typeof block !== 'object' || block === null || Array.isArray(block)) return {}
  const resolved: Record<string, SettingValue> = {}
  // Sorted, because this is simulation state in everything but name: the resolved map is read by
  // the game's constructor, and deterministic iteration is a standing rule of the core even where
  // — as here — nothing currently iterates it.
  for (const key of Object.keys(block as Record<string, unknown>).sort()) {
    const spec = (block as Record<string, unknown>)[key]
    if (typeof spec !== 'object' || spec === null) continue
    const value = (spec as { default?: unknown }).default
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      resolved[key] = value
    }
  }
  return resolved
}

/**
 * A numeric setting, or the fallback.
 *
 * **The fallback is what the engine did before this file existed**, at every call site, and that is
 * the whole of the compatibility story: a machine that declares nothing gets the old constant, a
 * machine that declares a value gets its value, and there is no third case.
 *
 * A non-finite declared number falls back rather than propagating. `NaN` reaching `ballsPerGame`
 * is a game that can never end, and a validator refuses one long before here — but this is in the
 * hot path of a constructor that has to work on a document a validator has not seen, because
 * `loadMachine` is what the *Builder* calls on every keystroke.
 */
export function settingNumber(
  settings: ResolvedSettings,
  id: string,
  fallback: number,
): number {
  const value = settings[id]
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** A boolean setting, or the fallback. */
export function settingFlag(settings: ResolvedSettings, id: string, fallback: boolean): boolean {
  const value = settings[id]
  return typeof value === 'boolean' ? value : fallback
}

/** A string setting, or the fallback. */
export function settingText(settings: ResolvedSettings, id: string, fallback: string): string {
  const value = settings[id]
  return typeof value === 'string' ? value : fallback
}
